"use client";

import NotFound from "../../../../common/NotFound";
import { useAppDispatch, useAppSelector } from "../../../../slice/hooks";
import { getActiveCart, getCartSource } from "../../../../slice/cart-slice";
import { useEffect, useMemo, useState } from "react";
import { useToken } from "../../../../../lib/context/auth-context";
import BreadcrumbMenu from "../../../../common/BreadcrumbMenu";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../../../../slice/accessOrg-slice";
import {
  extractProductChecksumFromSlug,
  getCategoryUrlPath,
  normalizeName,
} from "../../../../../lib/util";
import { fetchPublicProductByChecksum } from "../../../../../lib/services/product-service";

const BaseProductPage = ({
  StoreBox,
  ProductDetails,
  SimilarProducts,
  ProductPageSkeleton,
  productBreadcrumbClassName,
  styles,
  ...props
}) => {
  const cart = useAppSelector(getActiveCart);
  const {
    urlData,
    showCart,
    storePath,
    accessPointId,
    organisation,
    accessPoint,
    accessPoints,
    serverToken,
    navLocation,
  } = props;

  const { token } = useToken();

  /* =========================
     RESOLVE PRODUCT (🔥 SOURCE OF TRUTH)
     ========================= */

  const resolvedProduct = useMemo(() => {
    if (!urlData) return null;
    return urlData.product ?? urlData.product ?? null;
  }, [urlData]);

  const [productRes, setProductRes] = useState(urlData?.product ?? null);
  const [loading, setLoading] = useState(!resolvedProduct);

  const [isSwitching, setSwitching] = useState(false);
  const dispatch = useAppDispatch();

  const resolvedAccessPoints = useMemo(() => {
    return accessPoints?.length ? accessPoints : urlData?.accessPoints || [];
  }, [accessPoints, urlData?.accessPoints]);

  const productAccessPointId =
    productRes?.accessPointId || urlData?.product?.accessPointId;
  const routeAccessPointId = accessPointId || urlData?.accessPointId;

  const resolvedAccessPoint = useMemo(() => {
    const expectedAccessPointId = productAccessPointId || routeAccessPointId;
    const matchedAccessPoint = resolvedAccessPoints.find(
      (point) => point.accessPointId === expectedAccessPointId,
    );

    return (
      matchedAccessPoint ||
      (urlData?.accessPoint?.accessPointId === expectedAccessPointId
        ? urlData.accessPoint
        : null) ||
      (accessPoint?.accessPointId === expectedAccessPointId
        ? accessPoint
        : null) ||
      urlData?.accessPoint ||
      accessPoint ||
      null
    );
  }, [
    accessPoint,
    urlData?.accessPoint,
    resolvedAccessPoints,
    productAccessPointId,
    routeAccessPointId,
  ]);

  const resolvedOrganisation = organisation || urlData?.organisation || null;
  const resolvedAccessPointId =
    routeAccessPointId || resolvedAccessPoint?.accessPointId;

  const marketplaceStoreLabel = resolvedOrganisation?.orgName
    ? `${resolvedOrganisation.orgName}`
    : productRes?.host?.name
      ? `${productRes.host.name}`
      : "";

  const marketplaceStorePath = useMemo(() => {
    const handle = productRes?.host?.handle;
    if (!handle) return "";

    const accessPointName =
      resolvedAccessPoint?.name || productRes?.accessPointName || "";
    const normalizedAccessPointName = normalizeName(accessPointName);

    if (
      !normalizedAccessPointName ||
      normalizedAccessPointName === "main-entrance"
    ) {
      return `store/${handle}`;
    }

    return `store/${handle}/${normalizedAccessPointName}`;
  }, [productRes?.accessPointName, productRes?.host?.handle, resolvedAccessPoint]);

  const marketplaceStoreUrlData = useMemo(
    () => ({
      organisation: resolvedOrganisation,
      accessPoint: resolvedAccessPoint,
      accessPoints: resolvedAccessPoints,
      accessPointId: resolvedAccessPointId,
    }),
    [
      resolvedOrganisation,
      resolvedAccessPoint,
      resolvedAccessPoints,
      resolvedAccessPointId,
    ],
  );

  const storeBreadcrumbLabel =
    resolvedOrganisation?.settings?.storeName ||
    resolvedOrganisation?.orgName ||
    productRes?.host?.name ||
    "Store";
  const productCategoryName = productRes?.productCategoryName || "";

  const productCategoryPath = useMemo(() => {
    if (!productCategoryName) return "";
    return getCategoryUrlPath(storePath, productCategoryName);
  }, [productCategoryName, storePath]);

  const productBreadcrumbItems = useMemo(
    () =>
      [
        {
          label: storeBreadcrumbLabel,
          path: "home",
          urlData: marketplaceStoreUrlData,
          active: false,
        },
        productCategoryName
          ? {
              label: productCategoryName,
              path: productCategoryPath,
              urlData: marketplaceStoreUrlData,
              active: false,
            }
          : null,
        {
          label: productRes?.name || "Product",
          active: true,
        },
      ].filter(Boolean),
    [
      marketplaceStoreUrlData,
      productCategoryName,
      productCategoryPath,
      productRes?.name,
      storeBreadcrumbLabel,
    ],
  );

  /* =========================
     SYNC STATE FROM URL DATA
     ========================= */

  useEffect(() => {
    if (!urlData) return;

    // Restore organisation context from snapshot
    if (urlData.organisation) {
      dispatch(setOrganisation(urlData.organisation));
    }

    if (urlData.accessPointId) {
      dispatch(setAccessPointId(urlData.accessPointId));
    }

    if (urlData.accessPoint) {
      dispatch(setAccessPoint(urlData.accessPoint));
    }

    if (Array.isArray(urlData.accessPoints)) {
      dispatch(setAccessPoints(urlData.accessPoints));
    }

    if (urlData.product) {
      setProductRes(urlData?.product);
    }
  }, [urlData]);

  useEffect(() => {
    if (resolvedProduct) {
      setProductRes(resolvedProduct);
      setLoading(false);
      setSwitching(false);
    } else {
      setProductRes(null);
      setLoading(true);
    }
  }, [resolvedProduct]);

  /* =========================
     FETCH FALLBACK (ONLY IF NEEDED)
     ========================= */

  const checkSum = useMemo(() => {
    if (typeof window === "undefined") return null;
    const slug = window.location.pathname.split("/").filter(Boolean);
    const productIndex = slug.indexOf("product");
    if (productIndex === -1 || slug.length <= productIndex + 1) return null;
    return extractProductChecksumFromSlug(slug[productIndex + 1]);
  }, []);

  useEffect(() => {
    if (resolvedProduct) return;

    if (!checkSum || !resolvedAccessPointId || !resolvedOrganisation) return;
    let cancelled = false;

    async function fetchProduct() {
      setLoading(true);

      try {
        const json = await fetchPublicProductByChecksum({
          profileId: resolvedOrganisation.orgId,
          checkSum,
          accessPointId: resolvedAccessPointId,
          token: token || serverToken,
        });

        if (!cancelled) {
          setProductRes(json?.data?.product || null);
        }
      } catch {
        if (!cancelled) setProductRes(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [
    resolvedProduct,
    checkSum,
    resolvedAccessPointId,
    resolvedOrganisation,
    token,
    serverToken,
  ]);

  /* =========================
     RENDER
     ========================= */

  return (
    <StoreBox {...props} storePath={storePath} showMarketDiv={true}>
      <div className={styles.productDetails}>
        {(loading || isSwitching) && <ProductPageSkeleton />}

        {!loading && !isSwitching && productRes && (
          <>
            {navLocation === "marketplace" && (
              <BreadcrumbMenu
                sauce="marketplace-store"
                storeLabel={marketplaceStoreLabel}
                storePath={marketplaceStorePath}
                storeUrlData={marketplaceStoreUrlData}
              />
            )}
            {navLocation !== "marketplace" && (
              <BreadcrumbMenu
                className={productBreadcrumbClassName}
                items={productBreadcrumbItems}
              />
            )}
            <ProductDetails
              cart={cart}
              product={productRes}
              showCart={showCart}
              enablePreview
              accessPoint={resolvedAccessPoint}
              organisation={resolvedOrganisation}
            />
          </>
        )}

        {!loading && !productRes && !isSwitching && <NotFound />}
      </div>

      <SimilarProducts
        product={productRes}
        setProduct={setProductRes}
        organisation={resolvedOrganisation}
        accessPoint={resolvedAccessPoint}
        accessPoints={resolvedAccessPoints}
        token={token || serverToken}
        storePath={storePath}
        isSwitching={isSwitching}
        setSwitching={setSwitching}
        navLocation={navLocation}
      />
    </StoreBox>
  );
};

export default BaseProductPage;
